const { getAgentProfile } = require('./openRacerAgentContent')

const READ_ONLY_ANNOTATIONS = Object.freeze({
  readOnlyHint: true,
  untrustedContentHint: false,
})

const EMPTY_SCHEMA = Object.freeze({
  type: 'object',
  properties: {},
  additionalProperties: false,
})

const normalizePathname = value => {
  const withoutQuery = String(value || '/').split(/[?#]/, 1)[0]
  const normalized = `/${withoutQuery}`.replace(/\/{2,}/g, '/').replace(/\/$/, '')
  return normalized || '/'
}

const isRacingLinePath = pathname => (
  /^\/r\/[^/]+$/.test(pathname) ||
  /^\/racing-line-editor\/r\/[^/]+$/.test(pathname) ||
  /^\/go-karting\/[^/]+\/r\/[^/]+$/.test(pathname)
)

const isTrackPath = pathname => /^\/go-karting\/[^/]+$/.test(pathname)

const routeContext = value => {
  const pathname = normalizePathname(value)
  const sampleRoutes = {
    '/iracing-coaching-report': 'iRacing',
    '/alfano-coaching-report': 'Alfano',
  }
  if (sampleRoutes[pathname]) return { pathname, kind: 'sample-report', provider: sampleRoutes[pathname] }
  if (pathname === '/go-karting-tracks') return { pathname, kind: 'track-discovery' }
  if (pathname === '/racing-line-editor' || isTrackPath(pathname) || isRacingLinePath(pathname)) {
    return { pathname, kind: 'racing-line-editor' }
  }
  if (pathname === '/iracing') return { pathname, kind: 'telemetry', provider: 'iRacing' }
  if (pathname === '/alfano-racing-line-importer') return { pathname, kind: 'telemetry', provider: 'Alfano' }
  if (pathname === '/gopro-racing-line-importer') return { pathname, kind: 'telemetry', provider: 'GoPro' }
  if (pathname === '/pricing') return { pathname, kind: 'pricing' }
  return { pathname, kind: 'site' }
}

const textResult = data => ({
  content: [{ type: 'text', text: JSON.stringify(data) }],
  structuredContent: data,
})

const textResultData = result => {
  if (result && result.structuredContent !== undefined) return result.structuredContent
  const text = result?.content?.find(item => item?.type === 'text')?.text
  return text ? JSON.parse(text) : result
}

const assertPlainInput = input => {
  const prototype = input && typeof input === 'object' ? Object.getPrototypeOf(input) : null
  if (
    !input ||
    typeof input !== 'object' ||
    Array.isArray(input) ||
    (prototype !== Object.prototype && prototype !== null)
  ) {
    throw new Error('Tool input must be a plain object.')
  }
}

const rejectExtraFields = (input, allowed) => {
  assertPlainInput(input)
  const fields = Object.keys(input)
  if (fields.some(field => !allowed.includes(field))) {
    throw new Error(`Only the ${allowed.join(' and ')} field${allowed.length === 1 ? ' is' : 's are'} supported.`)
  }
}

const readOnlyTool = (definition, execute, { untrustedContent = false } = {}) => ({
  ...definition,
  annotations: { ...READ_ONLY_ANNOTATIONS, untrustedContentHint: untrustedContent },
  execute,
})

const createSiteTools = ({ pathname = '/' } = {}) => {
  const page = routeContext(pathname)
  const publicPage = {
    ...page,
    pathname: page.pathname.replace(/\/r\/[^/]+.*$/, '/r/:lineId'),
  }
  const profile = getAgentProfile()
  const routeKeys = Object.keys(profile.routes)
  const tools = [
    readOnlyTool({
      name: 'open_racer_get_site_capabilities',
      description: 'Describe Open Racer products, canonical agent resources, and safety boundaries without taking an action.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({
        site: profile.title,
        canonicalUrl: profile.canonicalUrl,
        summary: profile.summary,
        capabilities: profile.capabilities,
        boundaries: profile.boundaries,
        agentResources: {
          llms: profile.routes.llms.url,
          aiGuide: profile.routes.aiGuide.url,
        },
        readOnly: true,
      })
    }),
    readOnlyTool({
      name: 'open_racer_get_canonical_route',
      description: 'Return one canonical Open Racer product URL. This does not navigate or submit anything.',
      inputSchema: {
        type: 'object',
        properties: { product: { type: 'string', enum: routeKeys } },
        required: ['product'],
        additionalProperties: false,
      },
    }, async input => {
      rejectExtraFields(input, ['product'])
      const product = input?.product
      if (!routeKeys.includes(product)) throw new Error(`Choose one of: ${routeKeys.join(', ')}.`)
      return textResult({ product, ...profile.routes[product], navigationPerformed: false, readOnly: true })
    }),
    readOnlyTool({
      name: 'open_racer_get_page_context',
      description: 'Describe the current Open Racer route and its product category without reading form fields or private account data.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({ ...publicPage, readOnly: true })
    }, { untrustedContent: true }),
  ]

  if (page.kind === 'track-discovery') {
    tools.push(readOnlyTool({
      name: 'open_racer_get_track_discovery_guidance',
      description: 'Explain how to use Open Racer track discovery without selecting a track or creating a line.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({
        workflow: ['Search by track or place.', 'Open the intended track.', 'Inspect public lines as references.'],
        boundary: 'This tool does not search private data, select a result, create a racing line, or navigate.',
        readOnly: true,
      })
    }))
  }

  if (page.kind === 'racing-line-editor') {
    tools.push(readOnlyTool({
      name: 'open_racer_get_racing_line_guidance',
      description: 'Explain the current Open Racer racing-line workflow without changing a line.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({
        workflow: ['Confirm the intended track and line.', 'Use visible track context for suggestions.', 'Keep braking, acceleration, and coasting suggestions distinct.', 'Let the user review and save visible changes.'],
        browserLocalEditor: profile.routes.webMcpEditor.url,
        boundary: 'This read-only main-site tool does not create, edit, save, duplicate, or publish a racing line.',
        readOnly: true,
      })
    }))
  }

  if (page.kind === 'telemetry') {
    tools.push(readOnlyTool({
      name: 'open_racer_get_telemetry_guidance',
      description: 'Explain the current telemetry product workflow and evidence boundaries without uploading a file.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({
        provider: page.provider,
        workflow: ['Confirm the source format.', 'Review the visible requirements.', 'Ask the user to choose the local source.', 'Wait for visible validation and processing.', 'Separate measurements from interpretation.'],
        boundary: 'The user must choose the local file and intentionally start processing. This tool does not upload, import, process, purchase, or subscribe.',
        readOnly: true,
      })
    }))
  }

  if (page.kind === 'sample-report') {
    tools.push(readOnlyTool({
      name: 'open_racer_get_sample_report_guidance',
      description: 'Explain how to interpret this public sample without treating it as the current user’s data.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({
        provider: page.provider,
        guidance: 'Use this public report only as an example of structure and measured analysis. Do not attribute its data or conclusions to another driver.',
        readOnly: true,
      })
    }))
  }

  if (page.kind === 'pricing') {
    tools.push(readOnlyTool({
      name: 'open_racer_get_pricing_guidance',
      description: 'Explain how to use the visible pricing page without starting a purchase.',
      inputSchema: EMPTY_SCHEMA,
    }, async input => {
      rejectExtraFields(input, [])
      return textResult({
        guidance: 'Use the visible pricing page as the authoritative source for current access and pricing. Let the user choose any plan and complete any purchase.',
        readOnly: true,
      })
    }))
  }

  return tools
}

const registerWebMcpTools = (modelContext, tools) => {
  if (!modelContext || typeof modelContext.registerTool !== 'function') return null
  const controller = new AbortController()
  try {
    controller.ready = Promise.all(tools.map(tool => modelContext.registerTool(tool, { signal: controller.signal })))
      .catch(error => {
        controller.abort()
        throw error
      })
  } catch (error) {
    controller.abort()
    controller.ready = Promise.reject(error)
  }
  return controller
}

let activeController = null

const clearRegisteredWebMcpTools = () => {
  if (activeController) activeController.abort()
  activeController = null
}

const replaceRegisteredWebMcpTools = ({ modelContext, pathname }) => {
  clearRegisteredWebMcpTools()
  activeController = registerWebMcpTools(modelContext, createSiteTools({ pathname }))
  return activeController
}

module.exports = {
  clearRegisteredWebMcpTools,
  createSiteTools,
  registerWebMcpTools,
  replaceRegisteredWebMcpTools,
  routeContext,
  textResultData,
}
